import type L from "leaflet";
import type { LatLngTuple } from "@fortex/api";

export type SnapCandidate = {
  id: number;
  kind: "vertex" | "segment";
  points: LatLngTuple[];
};

export type PixelPoint = { x: number; y: number };

export type Projector = {
  project(point: LatLngTuple): PixelPoint;
  unproject(pixel: PixelPoint): LatLngTuple;
};

export type SnapResult = {
  point: LatLngTuple;
  snapped: boolean;
  kind: "vertex" | "segment" | null;
  candidate: SnapCandidate | null;
  distancePx: number;
};

function isMap(value: L.Map | Projector): value is L.Map {
  return typeof (value as L.Map).latLngToLayerPoint === "function";
}

export function mapProjector(map: L.Map): Projector {
  return {
    project: point => {
      const pixel = map.latLngToLayerPoint(point);

      return { x: pixel.x, y: pixel.y };
    },
    unproject: pixel => {
      const latlng = map.layerPointToLatLng([pixel.x, pixel.y]);

      return [latlng.lat, latlng.lng];
    }
  };
}

export function toProjector(value: L.Map | Projector): Projector {
  return isMap(value) ? mapProjector(value) : value;
}

function pixelDistance(a: PixelPoint, b: PixelPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function nearestVertex(candidates: SnapCandidate[], target: LatLngTuple, projector: L.Map | Projector): { point: LatLngTuple; distancePx: number; candidate: SnapCandidate } | null {
  const projection = toProjector(projector);
  const targetPixel = projection.project(target);
  let best: { point: LatLngTuple; distancePx: number; candidate: SnapCandidate } | null = null;

  for (const candidate of candidates) {
    if (candidate.kind !== "vertex") continue;

    for (const point of candidate.points) {
      const distance = pixelDistance(projection.project(point), targetPixel);

      if (best === null || distance < best.distancePx) best = { point, distancePx: distance, candidate };
    }
  }

  return best;
}

export function nearestOnSegment(from: LatLngTuple, to: LatLngTuple, target: LatLngTuple, projector: L.Map | Projector): { point: LatLngTuple; distancePx: number; fraction: number } {
  const projection = toProjector(projector);
  const a = projection.project(from);
  const b = projection.project(to);
  const p = projection.project(target);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  const fraction = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
  const projected = { x: a.x + fraction * dx, y: a.y + fraction * dy };

  return { point: projection.unproject(projected), distancePx: pixelDistance(projected, p), fraction };
}

export function nearestSegment(candidates: SnapCandidate[], target: LatLngTuple, projector: L.Map | Projector): { point: LatLngTuple; distancePx: number; candidate: SnapCandidate } | null {
  let best: { point: LatLngTuple; distancePx: number; candidate: SnapCandidate } | null = null;

  for (const candidate of candidates) {
    if (candidate.kind !== "segment" || candidate.points.length < 2) continue;

    const result = nearestOnSegment(candidate.points[0], candidate.points[1], target, projector);

    if (best === null || result.distancePx < best.distancePx) best = { point: result.point, distancePx: result.distancePx, candidate };
  }

  return best;
}

export function snapPoint(candidates: SnapCandidate[], target: LatLngTuple, projector: L.Map | Projector, radiusPx: number, shiftHeld: boolean): SnapResult {
  const none: SnapResult = { point: target, snapped: false, kind: null, candidate: null, distancePx: Number.POSITIVE_INFINITY };

  if (shiftHeld || candidates.length === 0 || radiusPx <= 0) return none;

  const vertex = nearestVertex(candidates, target, projector);

  if (vertex !== null && vertex.distancePx <= radiusPx) return { point: vertex.point, snapped: true, kind: "vertex", candidate: vertex.candidate, distancePx: vertex.distancePx };

  const segment = nearestSegment(candidates, target, projector);

  if (segment !== null && segment.distancePx <= radiusPx) return { point: segment.point, snapped: true, kind: "segment", candidate: segment.candidate, distancePx: segment.distancePx };

  return none;
}

export function nearestSegmentIndex(points: LatLngTuple[], target: LatLngTuple, projector: L.Map | Projector, closed: boolean): { index: number; point: LatLngTuple; distancePx: number } | null {
  let best: { index: number; point: LatLngTuple; distancePx: number } | null = null;
  const count = closed ? points.length : points.length - 1;

  for (let index = 0; index < count; index++) {
    const from = points[index];
    const to = points[(index + 1) % points.length];
    const result = nearestOnSegment(from, to, target, projector);

    if (best === null || result.distancePx < best.distancePx) best = { index, point: result.point, distancePx: result.distancePx };
  }

  return best;
}
